import React from 'react'
import { Tooltip } from '@surecompanies/core_components'
import { CopyOutlined } from '@ant-design/icons'
import {
  FieldsGrid,
  FieldGroup,
  FieldLabel,
  FieldValue,
  CopyableValue,
  CopyIcon,
} from './PaymentDetailModal.styles'

/** Field item for data-driven section grids: title, value, optional copy/tooltip */
export interface DetailFieldItem {
  title: string
  value: React.ReactNode
  /** When true, show copy icon and copy copyValue (or string value) to clipboard */
  isCopy?: boolean
  /** String to copy when isCopy; defaults to value if it's a string */
  copyValue?: string
  /** Optional tooltip for the value (e.g. full text when value is truncated) */
  tooltipTitle?: string
  /** Optional style for the field group */
  style?: React.CSSProperties
  onClick?: () => void
}

interface DetailFieldGridProps {
  fields: DetailFieldItem[]
  onCopy: (text: string) => void
}

export function DetailFieldGrid({ fields, onCopy }: DetailFieldGridProps) {
  const clickableTextStyle: React.CSSProperties = {
    color: '#1890ff',
    textDecoration: 'underline',
    cursor: 'pointer',
  }

  return (
    <FieldsGrid>
      {fields.map((field) => {
        const copyStr =
          field.copyValue ??
          (typeof field.value === 'string' ? field.value : undefined)
        const canCopy = field.isCopy && copyStr != null && copyStr !== 'N/A'
        const hasOnClick = Boolean(field.onClick)
        const isClickable = hasOnClick || canCopy

        const handleValueClick = () => {
          if (field.onClick) {
            field.onClick()
            return
          }
          if (canCopy) {
            onCopy(copyStr as string)
          }
        }

        const content = (
          <>
            {field.tooltipTitle != null && isClickable ? (
              <Tooltip placement="topLeft" title={field.tooltipTitle}>
                <CopyableValue
                  onClick={handleValueClick}
                  style={hasOnClick ? clickableTextStyle : undefined}
                >
                  {field.value}
                </CopyableValue>
              </Tooltip>
            ) : isClickable ? (
              <CopyableValue
                onClick={handleValueClick}
                style={hasOnClick ? clickableTextStyle : undefined}
              >
                {field.value}
              </CopyableValue>
            ) : (
              field.value
            )}
            {canCopy && (
              <CopyIcon
                onClick={() => {
                  if (field.onClick) {
                    field.onClick()
                  } else {
                    onCopy(copyStr)
                  }
                }}
              >
                <CopyOutlined style={{ fontSize: 12 }} />
              </CopyIcon>
            )}
          </>
        )
        return (
          <FieldGroup key={field.title} style={field.style}>
            <FieldLabel>{field.title}</FieldLabel>
            <FieldValue>{content}</FieldValue>
          </FieldGroup>
        )
      })}
    </FieldsGrid>
  )
}
